import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import * as apiSettingsService from '@/services/apiSettingsService';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const useApiSettings = (userId?: string) => {
  const { profile, isLoading } = useProfile(userId);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Invalidate profile query to refresh data from server
  const invalidateProfile = () => {
    queryClient.invalidateQueries({ queryKey: ['profile', userId] });
  };

  const regenerateApiKeyMutation = useMutation({
    mutationFn: apiSettingsService.regenerateApiKey,
    onSuccess: () => {
      invalidateProfile();
      toast({
        title: "Успех",
        description: "API ключ был успешно регенерирован.",
      });
    },
    onError: (error: Error) => {
      console.error("Mutation error (regenerateApiKey):", error);
      toast({
        title: "Ошибка",
        description: `Не удалось регенерировать API ключ: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const regenerateWebhookSecretMutation = useMutation({
    mutationFn: apiSettingsService.regenerateWebhookSecret,
    onSuccess: () => {
      invalidateProfile();
      toast({
        title: "Успех",
        description: "Webhook секрет был успешно регенерирован.",
      });
    },
    onError: (error: Error) => {
      console.error("Mutation error (regenerateWebhookSecret):", error);
      toast({
        title: "Ошибка",
        description: `Не удалось регенерировать Webhook секрет: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateWebhookUrlMutation = useMutation({
    mutationFn: apiSettingsService.updateWebhookUrl,
    onSuccess: () => {
      invalidateProfile();
      toast({
        title: "Успех",
        description: "Webhook URL был успешно обновлен.",
      });
    },
    onError: (error: Error) => {
      console.error("Mutation error (updateWebhookUrl):", error);
      toast({
        title: "Ошибка",
        description: `Не удалось обновить Webhook URL: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    apiKey: profile?.api_key || '',
    webhookUrl: profile?.webhook_url || '',
    webhookSecret: profile?.webhook_secret || '',
    isLoading,
    isRegeneratingApiKey: regenerateApiKeyMutation.isPending,
    isRegeneratingWebhookSecret: regenerateWebhookSecretMutation.isPending,
    isUpdatingWebhookUrl: updateWebhookUrlMutation.isPending,
    handleRegenerateApiKey: regenerateApiKeyMutation.mutateAsync,
    handleRegenerateWebhookSecret: regenerateWebhookSecretMutation.mutateAsync,
    handleUpdateWebhookUrl: updateWebhookUrlMutation.mutateAsync,
  };
};